/**
 * Utilitários para tratamento de avatares
 */

const WORKSPACE_AVATAR_MAX_BYTES = 5 * 1024 * 1024;
const WORKSPACE_AVATAR_MAX_EDGE = 256;
const WORKSPACE_AVATAR_ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

export const getAvatarUrl = (avatarBase64?: string, fallbackUrl?: string): string => {
  // Se há avatarBase64 válido
  if (avatarBase64 && avatarBase64.trim() !== '' && avatarBase64 !== 'null' && avatarBase64 !== 'undefined') {
    // Se é um caminho do S3 (começa com "profiles/")
    if (avatarBase64.startsWith('profiles/')) {
      // Para caminhos S3, retornar uma URL que será tratada pelo componente
      // O componente deve usar o hook useAvatar para buscar a URL real
      return `s3://${avatarBase64}`;
    }
    // Se já tem o prefixo data:image (qualquer tipo)
    if (avatarBase64.startsWith('data:image/')) {
      return avatarBase64;
    }
    // Se é uma URL HTTP/HTTPS (URL do S3)
    if (avatarBase64.startsWith('http://') || avatarBase64.startsWith('https://')) {
      return avatarBase64;
    }
    // Se não tem prefixo, adicionar (assumindo JPEG como padrão)
    return `data:image/jpeg;base64,${avatarBase64}`;
  }
  
  // Imagem padrão quando não há avatar
  return fallbackUrl || 'https://static.wixstatic.com/media/f70fc3_ded6f3a55dcd40299a3d3d099f55db4e~mv2.png';
};

export const getInitials = (name?: string): string => {
  if (!name || name.trim() === '') return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
};

export const getDirectWorkspaceAvatarSrc = (avatar?: string): string | undefined => {
  if (!avatar || avatar.trim() === '')
  {
    return undefined;
  }

  if (avatar.startsWith('data:'))
  {
    return avatar;
  }

  if (avatar.startsWith('http://') || avatar.startsWith('https://'))
  {
    if (/\/workspaces\/[^/]+\/avatar/i.test(avatar))
    {
      return undefined;
    }

    return avatar;
  }

  return undefined;
};

export const getWorkspaceAvatarSrc = (workspaceId?: string, avatar?: string): string | undefined => {
  const direct = getDirectWorkspaceAvatarSrc(avatar);
  if (direct)
  {
    return direct;
  }

  if (!avatar || avatar.trim() === '')
  {
    return undefined;
  }

  if (!workspaceId)
  {
    return undefined;
  }

  const base = (process.env.REACT_APP_API_URL || 'https://localhost:7180/api').replace(/\/$/, '');
  return `${base}/workspaces/${workspaceId}/avatar?v=${encodeURIComponent(avatar)}`;
};

export const validateWorkspaceAvatarFile = (file: File): string | undefined => {
  if (!WORKSPACE_AVATAR_ALLOWED_TYPES.includes(file.type))
  {
    return 'Formato de imagem inválido. Use JPG, PNG ou WebP.';
  }

  if (file.size > WORKSPACE_AVATAR_MAX_BYTES)
  {
    return 'A imagem do assistente deve ter no máximo 5 MB.';
  }

  return undefined;
};

export const readImageFileAsDataUrl = (file: File, maxEdge = WORKSPACE_AVATAR_MAX_EDGE): Promise<string> => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const longestEdge = Math.max(image.width, image.height);
      const scale = longestEdge > maxEdge ? maxEdge / longestEdge : 1;
      const width = Math.max(1, Math.round(image.width * scale));
      const height = Math.max(1, Math.round(image.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');
      if (!context)
      {
        reject(new Error('Não foi possível processar a imagem.'));
        return;
      }

      context.drawImage(image, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Não foi possível ler a imagem selecionada.'));
    };

    image.src = objectUrl;
  });
};
